import { useDoctorProfile, useRetriveCart } from 'reactQuery/queries';
import { useFetchMarketingProjects } from 'reactQuery/queries/marketingProjects';
import { useDesignHuddleProjectList } from 'reactQuery/queries/designHuddle';
import React, { useMemo } from 'react';
import { DesignHuddleEditor, Styled } from './';
import {
  ColumnFiltersState,
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useInfiniteScroll } from 'utils/hooks';
import { MarketingProjectClientResponse } from 'types';
import {
  fuzzyFilter,
  renderDate,
  renderHeader,
  Table,
  TBody,
  TCell,
  TDownloadIcon,
  THead,
  THeadItem,
  TRow,
} from 'components/Table';
import { FetchMoreLoader } from 'components/ProductListing/ProductListing.styles';
import { LoadingBar, LoadingSpinner } from 'components/core';
import { AiOutlineEdit } from 'react-icons/ai';
import { useDeleteProject } from 'reactQuery/mutations';
import { useI18n } from 'contexts/i18n';

const DesignHuddleProjectList = () => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [, setExpanded] = React.useState<ExpandedState>({});
  const [, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const i18n = useI18n();
  const { data: doctorProfile, isLoading: isDoctorProfileLoading } =
    useDoctorProfile();

  const { data: currentCart } = useRetriveCart();
  const currentCartProjectIds = currentCart?.lineItems.map(
    lineItem => lineItem.dhProjectId
  );

  const marketingProjects = useFetchMarketingProjects({
    includeThumbnail: true,
    perPageSize: 10,
    currentPageNumber: 1,
    status: 'draft',
  });
  const deleteProjectMutation = useDeleteProject(doctorProfile?.userName, true);

  const deleteProject = (projectId: string) => {
    deleteProjectMutation.mutate({
      projectId: projectId,
      showNotification: true,
    });
  };

  const {
    setDownloadProjectId,
    onCloseEditor,
    activeProjectId,
    activeSKU,
    showCustomizationModal,
    onSelectProject,
  } = useDesignHuddleProjectList();

  const fetchElementRef = useInfiniteScroll(
    marketingProjects.isLoading,
    marketingProjects.isFetching,
    marketingProjects.hasNextPage as boolean,
    marketingProjects.fetchNextPage
  );

  const flattenedDrafts = useMemo(() => {
    let flattendData = marketingProjects.data
      ? marketingProjects.data.pages.flatMap(item =>
          //@ts-ignore
          'reviews' in item ? item.reviews : item
        )
      : [];
    flattendData = flattendData.filter(
      (draft: any) =>
        !currentCartProjectIds?.find(projectId => projectId === draft.projectId)
    );
    return flattendData;
  }, [marketingProjects.data]);

  const columnHelper = createColumnHelper<MarketingProjectClientResponse>();
  const columns = [
    columnHelper.accessor('thumbnailUrl', {
      header: () => i18n['Drafts'],
      cell: info => (
        <Styled.ThumbnailContainer>
          <Styled.Thumbnail
            onClick={() =>
              onSelectProject(
                info.row.original.projectId,
                info.row.original.sku
              )
            }
          >
            <img
              src={info.getValue()}
              alt={`${info.row.original.projectName} thumbnail`}
              width="80px"
              height="80px"
            />
            <Styled.EditIcon>
              <AiOutlineEdit size="24px" />
            </Styled.EditIcon>
          </Styled.Thumbnail>
        </Styled.ThumbnailContainer>
      ),
      size: 50,
    }),
    columnHelper.accessor('projectName', {
      header: info =>
        renderHeader(i18n['Draft name'], info.column.getIsSorted()),
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('creationDate', {
      header: info =>
        renderHeader(i18n['Creation Date'], info.column.getIsSorted()),
      cell: info => renderDate(info.getValue()) ?? info.getValue(),
    }),
    columnHelper.accessor('status', {
      header: () => '',
      cell: info => {
        const isDownloadable =
          !info.renderValue() ||
          info.renderValue() === 'system approved' ||
          info.renderValue() === 'approved';
        const isDeleting =
          deleteProjectMutation.variables?.projectId ===
            info.row.original.projectId && deleteProjectMutation.isLoading;
        return (
          <div style={{ textAlign: 'right', paddingRight: '20px' }}>
            {isDownloadable ? (
              <TDownloadIcon
                onClick={() =>
                  setDownloadProjectId(info.row.original.projectId as string)
                }
              />
            ) : null}
            {info.renderValue() === 'draft' && (
              <>
                <Styled.IconsContainer $isDeleting={isDeleting}>
                  {isDeleting ? (
                    <div>
                      <Styled.DeleteLoadingSpinner />
                    </div>
                  ) : (
                    <Styled.DeleteIcon
                      onClick={() => deleteProject(info.row.original.projectId)}
                      size="24px"
                    />
                  )}
                </Styled.IconsContainer>
              </>
            )}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: flattenedDrafts,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onExpandedChange: setExpanded,
    getSubRows: (row: any) => row.subRows,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugHeaders: true,
    debugColumns: false,
  });

  const isDraftHistory = flattenedDrafts.length > 0;

  if (marketingProjects.isError) {
    const errorMessageString = i18n['Error while fetching Download History Data'];
    console.error(errorMessageString, marketingProjects.error);
    return <div>{errorMessageString}</div>;
  }

  return (
    <>
      <Table>
        <THead>
          {table.getHeaderGroups().map(headerGroup => (
            <TRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <THeadItem key={header.id}>
                  {header.isPlaceholder ? null : (
                    <div
                      className={
                        header.column.getCanSort()
                          ? 'cursor-pointer select-none'
                          : ''
                      }
                      role={header.column.getCanSort() ? 'button' : undefined}
                      tabIndex={header.column.getCanSort() ? 0 : undefined}
                      aria-sort={
                        header.column.getIsSorted() === 'asc'
                          ? 'ascending'
                          : header.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : header.column.getCanSort()
                          ? 'none'
                          : undefined
                      }
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={e => {
                        if (
                          header.column.getCanSort() &&
                          (e.key === 'Enter' || e.key === ' ')
                        ) {
                          e.preventDefault();
                          header.column.getToggleSortingHandler()?.(e);
                        }
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        /* INFO: ascend and descend logic handled fully at
                                                                                                                                            the header rendering, to make the code and logic less scattered, and more readable */
                        asc: '',
                        desc: '',
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </THeadItem>
              ))}
            </TRow>
          ))}
        </THead>
        <TBody>
          {marketingProjects.isLoading ? null : (
            <>
              {table.getExpandedRowModel().rows.map((row, rowIndex) => {
                return (
                  <TRow key={row.id}>
                    {row.getVisibleCells().map(cell => {
                      const shouldFetchData =
                        rowIndex ===
                        table.getExpandedRowModel().rows.length - 9;
                      return (
                        <TCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          {shouldFetchData && <span ref={fetchElementRef} />}
                        </TCell>
                      );
                    })}
                  </TRow>
                );
              })}
            </>
          )}
        </TBody>
      </Table>

      {marketingProjects?.isFetchingNextPage && (
        <FetchMoreLoader>
          <LoadingSpinner />
        </FetchMoreLoader>
      )}
      {
        // Loading indicator displayed outside <table> element, because div cannot be part of <table> element
        marketingProjects.isLoading ||
        isDoctorProfileLoading ||
        typeof doctorProfile === 'undefined' ? (
          <LoadingBar />
        ) : null
      }
      {!isDraftHistory && !marketingProjects.isLoading
        ? i18n['No Drafts found.']
        : null}

      {showCustomizationModal && (
        <DesignHuddleEditor
          projectId={activeProjectId}
          onCloseEditor={onCloseEditor}
          sku={activeSKU}
          mode={'update'}
        />
      )}
    </>
  );
};

export default DesignHuddleProjectList;
